import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../services/apiClient';
import { sequenceService } from '../../services/sequenceService';
import { emailService, type EmailAccountConnection } from '../../services/emailService';
import type { EnrollBlockReason, OutreachSequence, SequenceEnrollment } from '../../types/sequence';
import { blockReasonMessage, channelLabel, isCallFirstBlock, planEnglish } from './copy';

interface Props {
  contact: { type: 'client' | 'lead'; id: string; name: string };
  onClose: () => void;
  onEnrolled?: (enrollment: SequenceEnrollment) => void;
}

const EnrollSequenceModal: React.FC<Props> = ({ contact, onClose, onEnrolled }) => {
  const [sequences, setSequences] = useState<OutreachSequence[]>([]);
  const [mailboxes, setMailboxes] = useState<EmailAccountConnection[]>([]);
  const [sequenceId, setSequenceId] = useState('');
  const [mailboxId, setMailboxId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [blockReason, setBlockReason] = useState<EnrollBlockReason | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [seqs, accounts] = await Promise.all([
          sequenceService.listSequences(),
          emailService.listAccounts(),
        ]);
        if (!mounted) return;
        setSequences(seqs);
        setMailboxes(accounts);
        if (seqs[0]) setSequenceId(seqs[0].id);
        const primary = accounts.find((a) => a.is_primary) ?? accounts[0];
        if (primary) setMailboxId(primary.id);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const selected = useMemo(() => sequences.find((s) => s.id === sequenceId) ?? null, [sequences, sequenceId]);

  const start = async () => {
    if (!sequenceId) return;
    setSubmitting(true);
    setBlockReason(null);
    try {
      const enrollment = await sequenceService.enroll(sequenceId, {
        contact_type: contact.type,
        contact_id: contact.id,
        email_account_id: mailboxId || undefined,
      });
      onEnrolled?.(enrollment);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setBlockReason((err.data?.reason as EnrollBlockReason) ?? 'already_enrolled');
      } else if (err instanceof ApiError && err.status === 404) {
        setBlockReason('not_found');
      } else {
        setBlockReason('already_enrolled');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-fg">Start a sequence</h2>
            <p className="mt-0.5 text-sm text-fg-muted">for {contact.name}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="btn-surface-muted rounded-lg px-2 py-1 text-sm">
            ✕
          </button>
        </div>

        {loading ? (
          <p className="mt-6 text-sm text-fg-muted">Loading…</p>
        ) : mailboxes.length === 0 ? (
          <div className="mt-6 rounded-xl border border-border bg-surface-muted p-4 text-sm text-fg-muted">
            No mailbox is connected. Connect one to start sending.
            <div className="mt-3">
              <Link to="/settings/email" className="btn-primary rounded-xl px-4 py-2 text-sm">
                Connect a mailbox
              </Link>
            </div>
          </div>
        ) : sequences.length === 0 ? (
          <div className="mt-6 rounded-xl border border-border bg-surface-muted p-4 text-sm text-fg-muted">
            You have no sequences yet.
            <div className="mt-3">
              <Link to="/sequences/new" className="btn-primary rounded-xl px-4 py-2 text-sm">
                Create a sequence
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-fg">Sequence</span>
              <select
                value={sequenceId}
                onChange={(e) => setSequenceId(e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg"
              >
                {sequences.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium text-fg">Send from</span>
              <select
                value={mailboxId}
                onChange={(e) => setMailboxId(e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg"
              >
                {mailboxes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.email_address}
                    {m.is_primary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            </label>

            {selected ? (
              <div className="rounded-xl border border-border bg-surface-muted p-3">
                <p className="text-sm font-medium text-fg">{planEnglish(selected.steps)}</p>
                <ol className="mt-2 space-y-1 text-sm text-fg-muted">
                  {selected.steps.map((step, i) => (
                    <li key={i}>
                      {i + 1}. {channelLabel(step.channel)} ·{' '}
                      {i === 0 ? 'immediately' : `+${step.delay_business_days} business days`}
                      {step.is_final ? ' (final touch)' : ''}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}

            {blockReason ? (
              <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                {blockReasonMessage(blockReason)}
                {isCallFirstBlock(blockReason) ? (
                  <div className="mt-2">
                    <Link to="/sequences/new" className="underline">
                      Create a call-first sequence
                    </Link>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="btn-secondary rounded-xl px-4 py-2 text-sm">
                Cancel
              </button>
              <button
                type="button"
                onClick={start}
                disabled={submitting || !sequenceId}
                className="btn-primary rounded-xl px-5 py-2 text-sm disabled:opacity-50"
              >
                {submitting ? 'Starting…' : 'Start sequence'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnrollSequenceModal;
